"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const FRAMEWORKS = [
    { name: "Bootstrap", logo: "logos:bootstrap", descKey: "Sass_everywhere_f1_d" },
    { name: "Bulma", logo: "logos:bulma", descKey: "Sass_everywhere_f2_d" },
    { name: "Foundation", logo: "logos:foundation", descKey: "Sass_everywhere_f3_d" },
    { name: "Angular", logo: "logos:angular", descKey: "Sass_everywhere_f4_d" },
    { name: "Vue", logo: "logos:vue", descKey: "Sass_everywhere_f5_d" },
    { name: "Ionic", logo: "logos:ionic", descKey: "Sass_everywhere_f6_d" },
];

const gridContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};

const cardItem = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

export default function SassEverywhere() {
    const t = useTranslations();

    return (
        <section id="sass-everywhere" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Sass_everywhere_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Sass_everywhere_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-12">
                {/* Glows */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-pink-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-pink-500/15 ring-1 ring-pink-400/30 px-3 py-1 mb-4">
                        <SolarIcon
                            name="magic-stick-3-bold"
                            className="w-3.5 h-3.5 text-pink-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink-300">
                            {t("Sass_everywhere_eyebrow")}
                        </span>
                    </div>

                    {/* Headline */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.12] tracking-tight mb-8 max-w-[640px]">
                        {t.rich("Sass_everywhere_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-pink-300 to-fuchsia-200 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    {/* Grille des frameworks */}
                    <motion.div
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
                    >
                        {FRAMEWORKS.map((fw) => (
                            <motion.div
                                key={fw.name}
                                variants={cardItem}
                                className="flex items-center gap-3 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-4 hover:bg-white/[0.07] transition-colors duration-200"
                            >
                                <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-white ring-1 ring-black/5 shrink-0">
                                    <Icon
                                        icon={fw.logo}
                                        className="w-6 h-6"
                                        aria-label={fw.name}
                                    />
                                </span>
                                <div className="min-w-0">
                                    <div className="text-base font-semibold text-white leading-tight truncate">
                                        {fw.name}
                                    </div>
                                    <div className="text-[12.5px] text-gray-300/80 leading-snug">
                                        {t(fw.descKey)}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Preuve sociale */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                        className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-5"
                    >
                        <div className="flex items-start gap-3">
                            <SolarIcon
                                name="verified-check-bold"
                                className="w-5 h-5 text-pink-300 shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-gray-300/90 leading-relaxed mb-0">
                                {t.rich("Sass_everywhere_proof", {
                                    b: (chunks) => (
                                        <strong className="text-white font-semibold">
                                            {chunks}
                                        </strong>
                                    ),
                                })}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
